import { Link } from "react-router";
import { useDesigns } from "@/api/useCatalogue";
import { StatusPill } from "@/components/StatusPill";
import { ArrowRightIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * My designs — drafts + approved templates ready to reorder.
 * Each row navigates to the customise screen for that design (Phase 2
 * will resolve a real design id to its draft state; Phase 1 stub just
 * routes to the kit catalogue entry).
 */
export function DesignsPage() {
  const { data: designs = [], isLoading } = useDesigns();

  return (
    <div className="mx-auto max-w-7xl px-8 py-10">
      <header className="mb-8">
        <div className="mb-2 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
          <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--studio)" }} />
          My designs
        </div>
        <h1 className="m-0 mb-2 text-xl font-semibold leading-tight text-fg-1">
          Designs in progress
        </h1>
        <p className="m-0 max-w-2xl text-base leading-relaxed text-fg-3">
          Saved drafts and approved designs ready to reorder.
        </p>
      </header>

      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        {isLoading ? (
          <div className="p-6 text-fg-3">Loading…</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
                {["Name", "Kit", "Version", "Status", "Updated", ""].map((h) => (
                  <th
                    key={h}
                    className="px-3.5 py-2.5 text-xxs font-semibold uppercase tracking-wider text-fg-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {designs.map((d) => (
                <tr
                  key={d.id}
                  className="cursor-pointer border-b border-[var(--border-1)] last:border-0 hover:bg-bg-1"
                >
                  <td className="px-3.5 py-3 text-sm font-medium text-fg-1">{d.name}</td>
                  <td className="px-3.5 py-3 font-mono text-xs tabular-nums text-fg-3">{d.kit}</td>
                  <td className="px-3.5 py-3 font-mono text-xs tabular-nums text-fg-3">{d.version}</td>
                  <td className="px-3.5 py-3">
                    <StatusPill status={d.status} />
                  </td>
                  <td className="px-3.5 py-3 text-sm tabular-nums text-fg-3">{d.updated}</td>
                  <td className="px-3.5 py-3 text-right">
                    <Link
                      to="/catalogue"
                      className={cn(
                        "inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-2xs font-semibold text-fg-2 no-underline transition-colors",
                        "hover:bg-[var(--bg-1)] hover:text-fg-1 active:bg-[var(--bg-2)]",
                        "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-1",
                      )}
                    >
                      Open <ArrowRightIcon width={11} height={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
